<template>
  <BlokkliProvider v-bind="blokkliProps">
    <Container class="mt-20 md:mt-30 lg:mt-60 xl:mt-90">
      <BackButton
        :external-fallback="{ name: 'pharmasuisse-events-overview' }"
        :text="$texts('backToEventsList')"
      />

      <div class="bg-white shadow-lg box-padding mt-30">
        <div class="flex flex-col md:flex-row gap-20 md:gap-30 text-base">
          <div
            class="w-full md:w-200 lg:w-[370px] xl:w-[480px] self-start shrink-0 aspect-square bg-gray-300"
          >
            <MediaImage v-if="image" v-bind="image" :image-style="imageStyle" />
          </div>
          <div class="flex-1 w-full flex flex-col">
            <div class="flex flex-wrap gap-10 mb-10">
              <template v-if="categories.length">
                <Tag
                  v-for="category in categories"
                  :key="category"
                  color="bg-gray-100"
                  :text="category"
                />
              </template>
            </div>
            <h1
              class="text-xl md:text-2xl lg:text-4xl xl:text-6xl font-semibold hyphens-auto"
            >
              {{ title }}
            </h1>

            <div v-if="lead" class="mb-20" v-html="lead" />

            <div v-if="registrationUrl || productUrl" class="mt-auto">
              <a
                v-if="registrationUrl"
                class="button is-primary"
                :href="registrationUrl"
                >{{ $texts('eventRegisterNow') }}</a
              >
              <NuxtLink
                v-else-if="productUrl"
                class="button is-primary"
                :to="productUrl"
                >{{ $texts('eventBuyNow') }}</NuxtLink
              >
            </div>
          </div>
        </div>
      </div>

      <div class="bg-white box-padding my-40 shadow-lg">
        <TitleWithIcon
          :title="$texts('productDetails')"
          class="mb-40"
        />
        <template v-if="body">
          <h3 class="text-lg">
            {{ $texts('productDescription') }}
          </h3>
          <div class="content text-lg" v-html="body" />
        </template>

        <template v-if="features.length">
          <h3 class="text-lg mt-30 mb-20">
            {{ $texts('productSpecs') }}
          </h3>
          <table class="structured-table w-full">
            <tbody>
              <tr v-for="(feature, index) in features" :key="index">
                <th class="md:w-1/3">{{ feature.label }}</th>
                <td
                  :class="{ 'whitespace-pre': feature.type === 'location' }"
                  v-html="feature.value"
                />
              </tr>
            </tbody>
          </table>
        </template>
      </div>
    </Container>

    <Container>
      <BlokkliField :list="paragraphs" name="field_paragraphs" />
    </Container>

    <Container>
      <BlokkliField
        :list="paragraphsFooter"
        name="field_paragraphs_footer"
        field-list-type="footer"
      />
    </Container>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type { NodeEventFragment } from '#graphql-operations'
import type { EventFeature } from '#pharmasuisse/app/components/Events/Teaser/index.vue'
import { falsy } from '#shared/helpers/type'
import { formatEventDate } from '~/helpers/formatEventDate'

const props = defineProps<NodeEventFragment>()

const { $texts } = useNuxtApp()

const registrationUrl = computed<string | null>(
  () => props.registration?.url?.path ?? null,
)
const productUrl = computed<string | null>(
  () => props.product?.url?.path ?? null,
)

const categories = computed<string[]>(
  () => props.eventCategory?.map((v) => v.label).filter(falsy) || [],
)

const features = computed<EventFeature[]>(() => {
  const features: (EventFeature | undefined | null)[] = []

  if (props.eventDate) {
    features.push({
      label: $texts('event.Date'),
      value: formatEventDate(props.eventDate),
    })
  }

  if (props.location) {
    features.push({
      label: $texts('event.location'),
      value: props.location,
      type: 'location',
    })
  }

  if (props.organiser) {
    features.push({
      label: $texts('event.organizer'),
      value: props.organiser,
    })
  }

  if (props.showPlacesAvailable && props.maximumAttendees) {
    features.push({
      label: $texts('event.capacity'),
      value: props.maximumAttendees,
    })
  }

  if (props.registrationPeriod) {
    features.push({
      label: $texts('event.registrationPeriod'),
      value: props.registrationPeriod.formatted,
    })
  }

  if (props.fphPoints) {
    features.push({
      label: $texts('event.phPoints'),
      value: props.fphPoints,
    })
  }

  return features.filter(falsy)
})

const imageStyle = defineImageStyle({
  type: 'sizes',
  aspectRatio: 1,
  sizes: {
    sm: 767,
    md: 200,
    lg: 370,
    xl: 480,
  },
})
</script>
